
import {
  defineComponent,
  PropType,
  ref,
  computed,
  watch,
  nextTick,
} from 'vue';
import { useI18n } from 'vue-i18n';
import useVuelidate from '@vuelidate/core';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import InputSwitch from 'primevue/inputswitch';
import SingleVoucherForm from './SingleVoucherForm.vue';
import ReuseCouponSelector from './ReuseCouponSelector.vue';
import ReusedVoucherPreview from './ReusedVoucherPreview.vue';
import { TemplatesCustom } from '@/types/generated-types/graphql';
import {
  MultiVoucherConfigItem,
  TemplateVouchersData,
  buildVoucherRules,
  reusePreviousCouponValues,
} from '@/composables/flows';
import type { ResolvedReusedVoucher, ResolvedParentVoucherConfig } from '@/composables/flows';

// Re-export types for backward compatibility
export type VoucherConfigItem = MultiVoucherConfigItem;
export type { TemplateVouchersData };

export interface TemplateVoucherInfo {
  templateId: string;
  templateName: string;
  hasConditionalVoucher?: boolean;
}

export const createDefaultVoucherConfig = (): VoucherConfigItem => ({
  method: 0, // No method selected - forces validation error
  descript: {},
  type: 0, // No type selected - forces validation error
  identifiant: '',
  amount: 0,
  duplicate: '',
  minorderamount: 0,
  validtime: 7, // 1 week
  prefix: '',
});

export default defineComponent({
  name: 'MultiVoucherConfig',

  components: {
    Dialog,
    Button,
    InputSwitch,
    SingleVoucherForm,
    ReuseCouponSelector,
    ReusedVoucherPreview,
  },

  props: {
    modelValue: {
      type: Object as PropType<TemplateVouchersData>,
      required: true,
    },

    templates: {
      type: Array as PropType<TemplatesCustom[]>,
      required: true,
      default: () => [],
    },

    formValidation: {
      type: Object,
      default: () => ({}),
    },

    allowAutomaticMethod: {
      type: Boolean,
      default: true,
    },

    showReusePreviousCouponField: {
      type: Boolean,
      default: false,
    },

    reusePreviousCouponOptions: {
      type: Array as PropType<Array<{ label: string; value: number }>>,
      default: () => [],
    },

    resolvedParentVoucher: {
      type: Object as PropType<ResolvedReusedVoucher>,
      default: () => ({ configs: [], hasMultiplePaths: false, notFound: true }),
    },

    resolvedExpiringVoucher: {
      type: Object as PropType<ResolvedReusedVoucher>,
      default: () => ({ configs: [], hasMultiplePaths: false, notFound: true }),
    },
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const { t } = useI18n();

    const REUSE_VALUES = reusePreviousCouponValues;

    // Modal state
    const showConfigModal = ref(false);
    const currentTemplateInfo = ref<TemplateVoucherInfo | null>(null);

    // Temporary voucher config for current template being edited
    const currentVoucherConfig = ref<VoucherConfigItem>(createDefaultVoucherConfig());
    // Temporary reuse value for current template being edited
    const currentReuseValue = ref<number>(REUSE_VALUES.CONFIGURE_NEW_VOUCHER);

    // Extract templates with vouchers (hasVoucher = true in config)
    const templatesWithVouchers = computed((): TemplateVoucherInfo[] => {
      const result: TemplateVoucherInfo[] = [];

      // Get funnels from root template config for template names
      const rootTemplate = props.templates[0];
      const rootConfig = rootTemplate ? JSON.parse(rootTemplate.template_config ?? '{}') : {};
      const funnels = rootConfig.funnels || [];

      // Build a map of template names from templates and funnels
      const templateNames: Record<string, string> = {};
      props.templates.forEach((t) => {
        templateNames[String(t.id_template)] = t.label || `Template ${t.id_template}`;
      });
      funnels.forEach((f: { id: number; name: string }) => {
        templateNames[String(f.id)] = f.name;
      });

      // Find templates with hasVoucher = true
      props.templates.forEach((template) => {
        const config = JSON.parse(template.template_config ?? '{}');
        if (config.hasVoucher === true) {
          const templateIdStr = String(template.id_template);
          result.push({
            templateId: templateIdStr,
            templateName: templateNames[templateIdStr] || `Template ${templateIdStr}`,
            hasConditionalVoucher: config.hasConditionalVoucher === true,
          });
        }
      });

      return result;
    });

    // Check if a template is reusing a previous coupon
    const isTemplateReusing = (templateId: string): boolean => {
      const config = props.modelValue[templateId];
      return !!config?.reuse && config.reuse !== REUSE_VALUES.CONFIGURE_NEW_VOUCHER;
    };

    // Check if the reuse selector should be shown for a specific template
    const shouldShowReuseForTemplate = (templateId: string): boolean => {
      // Always show if parent-based showReusePreviousCouponField is true
      if (props.showReusePreviousCouponField) return true;
      // Also show for non-first templates (they can reuse from a preceding template in the same box)
      const index = templatesWithVouchers.value.findIndex((ti) => ti.templateId === templateId);
      return index > 0;
    };

    // Get the reuse options for a specific template
    const getOptionsForTemplate = (templateId: string): Array<{ label: string; value: number }> => {
      const index = templatesWithVouchers.value.findIndex((ti) => ti.templateId === templateId);
      // First template: use parent-provided options as-is
      if (index === 0) return props.reusePreviousCouponOptions;
      // Subsequent templates: ensure "reuse from previous box" is always in the options
      const options = [...props.reusePreviousCouponOptions];
      const hasReuseOption = options.some((o) => o.value === REUSE_VALUES.REUSE_FROM_PREVIOUS_BOX);
      if (!hasReuseOption) {
        options.push({
          label: t('automatedScenarios.actions.sending_channel.template_config.reuse_a_previous_coupon.options.option_2'),
          value: REUSE_VALUES.REUSE_FROM_PREVIOUS_BOX,
        });
      }
      return options;
    };

    // Current modal state for reuse selector (computed per-template when modal opens)
    const currentShowReuse = ref(false);
    const currentReuseOptions = ref<Array<{ label: string; value: number }>>([]);

    // Resolved voucher for the currently open modal template
    // Handles intra-box reuse (from preceding template) vs inter-step reuse (from parent step)
    const currentModalResolvedVoucher = computed((): ResolvedReusedVoucher => {
      if (!currentTemplateInfo.value) {
        return { configs: [], hasMultiplePaths: false, notFound: true };
      }

      // "Use expiring discount voucher" → show expiring voucher filter info
      if (currentReuseValue.value === REUSE_VALUES.USE_EXPIRING_VOUCHER) {
        return props.resolvedExpiringVoucher;
      }

      const currentIndex = templatesWithVouchers.value.findIndex(
        (ti) => ti.templateId === currentTemplateInfo.value!.templateId,
      );

      // First template in the box → use parent step resolution
      if (currentIndex <= 0) {
        return props.resolvedParentVoucher;
      }

      // Non-first template → look backwards for a preceding template that actually configures
      for (let i = currentIndex - 1; i >= 0; i--) {
        const prevTemplateId = templatesWithVouchers.value[i].templateId;
        const prevConfig = props.modelValue[prevTemplateId];
        if (prevConfig && (!prevConfig.reuse || prevConfig.reuse === REUSE_VALUES.CONFIGURE_NEW_VOUCHER)) {
          return {
            configs: [{
              sourceStepId: 'same-box',
              sourceStepType: 'same-box',
              sourceLabel: templatesWithVouchers.value[i].templateName,
              voucherConfigs: { [prevTemplateId]: prevConfig },
              depth: 0,
            }],
            hasMultiplePaths: false,
            notFound: false,
          };
        }
      }

      // All preceding templates also reuse → chain to parent step
      return props.resolvedParentVoucher;
    });

    // Get the label for a reusing template's current reuse option
    const getTemplateReuseLabel = (templateId: string): string => {
      const config = props.modelValue[templateId];
      if (!config?.reuse) return '';
      // Check in parent-provided options first, then check the "reuse from previous box" label as fallback
      const option = props.reusePreviousCouponOptions.find((o) => o.value === config.reuse);
      if (option) return option.label;
      // Fallback for non-first templates where the option was added dynamically
      if (config.reuse === REUSE_VALUES.REUSE_FROM_PREVIOUS_BOX) {
        return t('automatedScenarios.actions.sending_channel.template_config.reuse_a_previous_coupon.options.option_2');
      }
      return '';
    };

    // Initialize model value with defaults for any missing templates
    const initializeModelValue = () => {
      const newValue = { ...props.modelValue };
      let hasChanges = false;

      templatesWithVouchers.value.forEach((templateInfo) => {
        if (!newValue[templateInfo.templateId]) {
          newValue[templateInfo.templateId] = {
            ...createDefaultVoucherConfig(),
            ...(templateInfo.hasConditionalVoucher ? { disabled: true } : {}),
          };
          hasChanges = true;
        } else if (!templateInfo.hasConditionalVoucher && newValue[templateInfo.templateId].disabled) {
          // Clean up stale disabled flag: template is no longer conditional
          newValue[templateInfo.templateId] = { ...newValue[templateInfo.templateId], disabled: false };
          hasChanges = true;
        }
      });

      if (hasChanges) {
        emit('update:modelValue', newValue);
      }
    };

    // Watch for template changes to initialize
    watch(
      () => props.templates,
      () => {
        initializeModelValue();
      },
      { immediate: true, deep: true },
    );

    // Open configuration modal for a template
    const openConfigModal = async (templateInfo: TemplateVoucherInfo) => {
      currentTemplateInfo.value = templateInfo;

      // Compute reuse selector visibility and options for this specific template
      currentShowReuse.value = shouldShowReuseForTemplate(templateInfo.templateId);
      currentReuseOptions.value = getOptionsForTemplate(templateInfo.templateId);

      // Copy current config to temporary state
      const existingConfig = props.modelValue[templateInfo.templateId];
      currentVoucherConfig.value = existingConfig
        ? { ...existingConfig }
        : createDefaultVoucherConfig();

      // Set current reuse value from config
      currentReuseValue.value = existingConfig?.reuse ?? REUSE_VALUES.CONFIGURE_NEW_VOUCHER;

      showConfigModal.value = true;

      // Trigger validation only if configuring new voucher
      if (currentReuseValue.value === REUSE_VALUES.CONFIGURE_NEW_VOUCHER) {
        await nextTick();
        v$Modal.value.$validate();
      }
    };

    // Modal validation error message
    const modalValidationError = ref<string>('');

    // Vuelidate rules for current voucher config (computed to update when config changes)
    const voucherRules = computed(() => buildVoucherRules(currentVoucherConfig.value));

    // Vuelidate instance for modal validation
    const v$Modal = useVuelidate(voucherRules, currentVoucherConfig);

    // Check if a template's voucher config is disabled (conditional voucher with toggle off)
    const isTemplateDisabled = (templateId: string): boolean => props.modelValue[templateId]?.disabled === true;

    // Toggle voucher config enabled/disabled for a conditional voucher template
    const toggleTemplateVoucher = (templateId: string, enabled: boolean) => {
      const newValue = { ...props.modelValue };
      if (!newValue[templateId]) {
        newValue[templateId] = createDefaultVoucherConfig();
      }
      newValue[templateId] = { ...newValue[templateId], disabled: !enabled };
      emit('update:modelValue', newValue);
    };

    // Check if a template has validation errors (from vuelidate or local validation)
    const hasTemplateValidationError = (templateId: string): boolean => {
      // No validation for disabled templates (conditional voucher with toggle off)
      if (isTemplateDisabled(templateId)) return false;

      // No validation error if template is reusing a previous coupon
      if (isTemplateReusing(templateId)) return false;

      // Check vuelidate errors from formValidation prop
      const v$ = props.formValidation;
      if (v$?.template_vouchers?.[templateId]?.$error) {
        return true;
      }

      // Also check if config exists and has valid method/type
      const templateConfig = props.modelValue[templateId];
      if (!templateConfig) return true; // No config = error

      // Check if method and type are configured (not 0)
      if (!templateConfig.method || templateConfig.method == 0) return true;
      if (!templateConfig.type || templateConfig.type == 0) return true;

      return false;
    };

    // Close configuration modal
    const closeConfigModal = () => {
      showConfigModal.value = false;
      currentTemplateInfo.value = null;
      modalValidationError.value = '';
      currentVoucherConfig.value = createDefaultVoucherConfig();
      currentReuseValue.value = REUSE_VALUES.CONFIGURE_NEW_VOUCHER;
      currentShowReuse.value = false;
      currentReuseOptions.value = [];
      v$Modal.value.$reset();
    };

    // Save voucher configuration
    const saveVoucherConfig = async () => {
      if (!currentTemplateInfo.value) return;

      // If reusing, skip voucher form validation
      if (currentReuseValue.value !== REUSE_VALUES.CONFIGURE_NEW_VOUCHER) {
        const newValue = { ...props.modelValue };
        const templateId = currentTemplateInfo.value.templateId;

        // Save with reuse value (keep existing config in case user switches back)
        newValue[templateId] = { ...currentVoucherConfig.value, reuse: currentReuseValue.value };

        emit('update:modelValue', newValue);
        closeConfigModal();
        return;
      }

      // Validate using Vuelidate before saving
      const isValid = await v$Modal.value.$validate();
      if (!isValid) {
        modalValidationError.value = t('automatedScenarios.actions.sending_channel.multi_vouchers_config.validation_error');
        return;
      }

      const newValue = { ...props.modelValue };
      const templateId = currentTemplateInfo.value.templateId;

      // Copy temporary config to model value with reuse = 0 (configure new)
      newValue[templateId] = { ...currentVoucherConfig.value, reuse: REUSE_VALUES.CONFIGURE_NEW_VOUCHER };

      emit('update:modelValue', newValue);
      closeConfigModal();
    };

    return {
      t,
      REUSE_VALUES,
      showConfigModal,
      currentTemplateInfo,
      currentVoucherConfig,
      currentReuseValue,
      currentShowReuse,
      currentReuseOptions,
      currentModalResolvedVoucher,
      templatesWithVouchers,
      modalValidationError,
      isTemplateReusing,
      isTemplateDisabled,
      toggleTemplateVoucher,
      getTemplateReuseLabel,
      openConfigModal,
      closeConfigModal,
      saveVoucherConfig,
      hasTemplateValidationError,
      v$Modal,
    };
  },
});
